import { App, Image, Spin } from 'antd';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MobileEmpty } from '@/components/mobile/MobileEmpty';
import {
  PROJECT_REPORT_PERIOD_LABELS,
  PROJECT_REPORT_STATUS_LABELS,
} from '@/constants/project-report';
import { ROUTES } from '@/constants/routes';
import { useProjectReportDetail } from '@/hooks/useProjectReportDetail';
import { useAuthStore } from '@/stores/auth.store';
import { formatProjectReportDate } from '@/utils/format-date';

type MobileVariant = 'leader' | 'employee' | 'top-leader';

interface ProjectReportDetailMobilePageProps {
  variant: MobileVariant;
}

function projectDetailRoute(variant: MobileVariant, projectId: string): string {
  if (variant === 'employee') {
    return ROUTES.mobile.employee.projectDetail(projectId);
  }
  return ROUTES.mobile.leader.projectDetail(projectId);
}

function projectsListRoute(variant: MobileVariant): string {
  if (variant === 'employee') {
    return ROUTES.mobile.employee.projects;
  }
  return ROUTES.mobile.leader.projects;
}

export function ProjectReportDetailMobilePage({ variant }: ProjectReportDetailMobilePageProps) {
  const { projectId, reportId } = useParams<{ projectId: string; reportId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const isTopLeaderView = variant === 'top-leader';

  const { loading, report, comments, imageUrls, canComment, createComment } =
    useProjectReportDetail(reportId);
  const currentUser = useAuthStore((state) => state.user);

  const [composing, setComposing] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const goBack = useCallback(() => {
    if (isTopLeaderView) {
      navigate(ROUTES.mobile.leader.projects, { replace: true });
      return;
    }
    if (projectId) {
      navigate(projectDetailRoute(variant, projectId), { replace: true });
      return;
    }
    navigate(projectsListRoute(variant), { replace: true });
  }, [isTopLeaderView, navigate, projectId, variant]);

  const handleSubmitComment = async () => {
    const content = commentText.trim();
    if (!content) {
      message.warning('请填写点评内容');
      return;
    }

    setSubmitting(true);
    try {
      await createComment(content);
      setCommentText('');
      setComposing(false);
    } catch (error) {
      if (error instanceof Error && error.message) {
        message.error(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!reportId) {
    return <MobileEmpty message={isTopLeaderView ? '无效的项目进展' : '无效的项目日报'} />;
  }

  if (!loading && !report) {
    return (
      <MobileEmpty
        message={isTopLeaderView ? '项目进展不存在或无权查看' : '项目日报不存在或无权查看'}
      />
    );
  }

  const isReceiver = Boolean(report && currentUser && report.createdBy.id === currentUser.id);
  const showComments = !isTopLeaderView;

  return (
    <div className="mobile-detail-page">
      <div className="mobile-detail-page__body">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin />
          </div>
        ) : report ? (
          <>
            <section className="mobile-section">
              <div className="mobile-detail-row">
                <span className="mobile-detail-label">
                  {isTopLeaderView ? '项目名称' : '项目'}
                </span>
                <span className="mobile-detail-value">{report.project.name}</span>
              </div>
              <div className="mobile-detail-row">
                <span className="mobile-detail-label">
                  {isTopLeaderView ? '汇报人' : '提交人'}
                </span>
                <span className="mobile-detail-value">{report.createdBy.name}</span>
              </div>
              <div className="mobile-detail-row">
                <span className="mobile-detail-label">
                  {isTopLeaderView ? '汇报时间' : '日期'}
                </span>
                <span className="mobile-detail-value">
                  {formatProjectReportDate(
                    report.reportDate,
                    report.createdAt,
                    report.reportPeriod,
                  )}
                </span>
              </div>
              {!isTopLeaderView ? (
                <>
                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">周期</span>
                    <span className="mobile-detail-value">
                      {PROJECT_REPORT_PERIOD_LABELS[report.reportPeriod]}
                    </span>
                  </div>
                  <div className="mobile-detail-row">
                    <span className="mobile-detail-label">状态</span>
                    <span className="mobile-detail-value">
                      {PROJECT_REPORT_STATUS_LABELS[report.status]}
                    </span>
                  </div>
                </>
              ) : null}
            </section>

            <section className="mobile-section">
              <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                {isTopLeaderView ? '项目进展' : '进展说明'}
              </div>
              <p className="mobile-detail-text">{report.progressContent}</p>
            </section>

            {report.images.length > 0 ? (
              <section className="mobile-section">
                <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                  进展图片（{report.images.length}）
                </div>
                <Image.PreviewGroup>
                  <div className="mobile-detail-images">
                    {report.images.map((image) => (
                      <Image
                        key={image.id}
                        width={100}
                        height={100}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                        src={imageUrls[image.id] || undefined}
                        alt={image.originalName}
                      />
                    ))}
                  </div>
                </Image.PreviewGroup>
              </section>
            ) : null}

            <section className="mobile-section">
              <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                问题与风险
              </div>
              <p className="mobile-detail-text">{report.problemContent?.trim() || '—'}</p>
            </section>

            <section className="mobile-section">
              <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                下一步计划
              </div>
              <p className="mobile-detail-text">{report.nextPlanContent?.trim() || '—'}</p>
            </section>

            {showComments ? (
              <section className="mobile-section">
                <div className="mobile-section__header">
                  <span className="mobile-section__title">点评记录</span>
                  <span className="mobile-section__meta">{comments.length} 条</span>
                </div>
                {comments.length === 0 ? (
                  <p className="mobile-hint" style={{ margin: 0, textAlign: 'center' }}>
                    暂无点评
                  </p>
                ) : (
                  comments.map((item) => (
                    <div key={item.id} className="mobile-comment-item">
                      <div className="mobile-comment-item__meta">
                        {item.author.name} · {new Date(item.createdAt).toLocaleString('zh-CN')}
                        {isReceiver ? (
                          <span className="mobile-tag" style={{ marginLeft: 8 }}>
                            {item.readStatus === 'READ' ? '已读' : '未读'}
                          </span>
                        ) : null}
                      </div>
                      <p className="mobile-comment-item__content">{item.content}</p>
                    </div>
                  ))
                )}
              </section>
            ) : null}

            {showComments && composing ? (
              <section className="mobile-section mobile-comment-compose">
                <div className="mobile-section__title" style={{ marginBottom: 8 }}>
                  填写点评
                </div>
                <textarea
                  className="mobile-comment-compose__input"
                  rows={5}
                  placeholder="请输入点评内容"
                  value={commentText}
                  onChange={(event) => setCommentText(event.target.value)}
                  maxLength={5000}
                />
                <div className="mobile-comment-compose__actions">
                  <button
                    type="button"
                    className="mobile-btn mobile-btn--ghost"
                    onClick={() => {
                      setComposing(false);
                      setCommentText('');
                    }}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="mobile-btn mobile-btn--primary"
                    disabled={submitting}
                    onClick={() => void handleSubmitComment()}
                  >
                    {submitting ? '提交中…' : '提交点评'}
                  </button>
                </div>
              </section>
            ) : null}
          </>
        ) : null}
      </div>

      {showComments && report && canComment && !composing ? (
        <footer className="mobile-detail-footer">
          <button
            type="button"
            className="mobile-btn mobile-btn--primary mobile-btn--block"
            onClick={() => setComposing(true)}
          >
            点评
          </button>
        </footer>
      ) : null}

      {(!showComments || !canComment) && report ? (
        <footer className="mobile-detail-footer">
          <button
            type="button"
            className="mobile-btn mobile-btn--ghost mobile-btn--block"
            onClick={goBack}
          >
            {isTopLeaderView ? '返回' : '返回项目'}
          </button>
        </footer>
      ) : null}
    </div>
  );
}
